/**
 * Pure helpers shared by every place that reads a founder's website:
 *  - extractFirstUrl: pull the first usable URL out of free-form user text
 *    (the onboarding chat uses it to stage a pasted site as a materials upload)
 *  - isPrivateHost: SSRF allow/deny check (site-reader crawl, materials route,
 *    founder-identity enrichment)
 *  - looksLikeSpaShell: heuristic to detect JS-rendered pages
 *
 * All exports here are dependency-free and safe in client bundles.
 *
 * Server-only HTML parsing and guarded fetching (cheerio) live in
 * src/lib/site-snapshot.server.ts; the bounded onboarding crawl lives in
 * src/lib/site-reader/.
 */

export type SiteSnapshot = {
  url: string;
  finalUrl: string;
  title: string | null;
  description: string | null;
  headings: string[];
  bodyExcerpt: string;
  fetchedAt: string;
};

const URL_REGEX = /\b((?:https?:\/\/)?[a-z0-9][a-z0-9-]*(?:\.[a-z0-9-]{2,})+(?:\/[^\s]*)?)/gi;
const SCHEME_BLOCK = /^(?:mailto|tel|sms|ftp|file|javascript):/i;

/**
 * Extensions that make a bare "name.ext" match URL_REGEX (the ".ext" reads as a
 * 2+ char TLD) and parse as a host. A founder's uploaded deck/doc/image
 * filename lands in a chat message; without this guard it fires a doomed
 * site fetch (getaddrinfo ENOTFOUND on e.g. "deck.pdf"). Only applied
 * to scheme-less candidates — an explicit https:// signals real intent.
 *
 * Deliberately excludes real public TLDs that are ALSO common file extensions
 * (`.zip`, `.mov`, `.md`) — rejecting a bare `startup.zip` / `studio.mov` would
 * block legitimate websites, and the filename false-positive for those is rarer
 * than the real-domain case. Everything here is a non-TLD file extension.
 */
const FILE_EXTENSION_TLDS = new Set([
  "pdf", "doc", "docx", "ppt", "pptx", "xls", "xlsx", "csv", "txt", "rtf",
  "key", "pages", "numbers", "png", "jpg", "jpeg", "gif", "webp", "svg",
  "heic", "bmp", "tiff", "rar", "gz", "tar", "mp4", "avi", "json",
]);

/**
 * Find the first usable URL in user text. Returns a normalized URL with scheme.
 * Adds https:// when the user types a bare domain like "foo.com". Skips
 * filename-looking candidates (a scheme-less TLD that is a file extension) and
 * amounts like "1.95m" (a scheme-less TLD with a digit), and keeps scanning,
 * so "uploaded deck.pdf; my site is acme.com" still finds acme.com. Returns
 * null when no plausible URL is present.
 */
export function extractFirstUrl(text: string): string | null {
  if (!text) return null;
  const trimmed = text.trim();
  if (SCHEME_BLOCK.test(trimmed)) return null;

  for (const match of trimmed.matchAll(URL_REGEX)) {
    const raw = match[1];
    const hadScheme = /^https?:\/\//i.test(raw);
    const candidate = hadScheme ? raw : `https://${raw}`;

    try {
      const parsed = new URL(candidate);
      if (parsed.protocol !== "http:" && parsed.protocol !== "https:") continue;
      if (!parsed.hostname.includes(".")) continue;
      // A scheme-less candidate is skipped (scanning continues for a real URL
      // later in the message) when its TLD label is a file extension (a
      // filename like "deck.pdf") or contains a digit (real TLDs are letters
      // only, so "1.95m" and "3.75", which parses as 3.0.0.75, are amounts;
      // LEV-2768).
      if (!hadScheme) {
        const tld = parsed.hostname.split(".").pop()?.toLowerCase() ?? "";
        if (FILE_EXTENSION_TLDS.has(tld) || /\d/.test(tld)) continue;
      }
      return parsed.toString();
    } catch {
      /* expected: URL parsing of candidate may fail */
      continue;
    }
  }

  return null;
}

/**
 * SSRF guard: refuse to fetch obviously private/internal hosts. Public DNS
 * resolution is still up to the underlying fetch; this catches the common
 * literal cases: localhost, RFC1918, link-local, loopback variants, and their
 * IPv6 forms (`::`, `::1`, fe80::/10, fc00::/7, and IPv4-mapped/compatible
 * addresses checked through the IPv4 rules). Accepts `url.hostname` as-is,
 * bracketed IPv6 included.
 */
export function isPrivateHost(hostname: string): boolean {
  const h = hostname.toLowerCase().replace(/^\[|\]$/g, "");
  if (h === "localhost" || h.endsWith(".localhost")) return true;
  if (h.endsWith(".local") || h.endsWith(".internal")) return true;

  if (h.includes(":")) return isPrivateIpv6(h);

  const ipv4 = parseIpv4(h);
  return ipv4 ? isPrivateIpv4(ipv4) : false;
}

const IPV4_LITERAL = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;

type Ipv4Octets = [number, number, number, number];

function parseIpv4(text: string): Ipv4Octets | null {
  const match = text.match(IPV4_LITERAL);
  return match ? (match.slice(1).map(Number) as Ipv4Octets) : null;
}

function isPrivateIpv4([a, b]: Ipv4Octets): boolean {
  if (a === 127) return true; // loopback
  if (a === 10) return true; // RFC1918
  if (a === 172 && b >= 16 && b <= 31) return true; // RFC1918
  if (a === 192 && b === 168) return true; // RFC1918
  if (a === 169 && b === 254) return true; // link-local
  if (a === 0) return true; // wildcard
  if (a >= 224) return true; // multicast / reserved
  return false;
}

/** Unbracketed, lower-cased IPv6 literal (a `%zone` suffix is ignored). A literal that cannot be read is blocked. */
function isPrivateIpv6(literal: string): boolean {
  const hextets = expandIpv6(literal.split("%")[0] ?? "");
  if (!hextets) return true; // fail closed: never fetch an address we could not classify
  const [h0, , , , , h5, h6, h7] = hextets;
  const leadingZero = hextets.slice(0, 5).every((x) => x === 0);
  if (leadingZero && h5 === 0 && h6 === 0 && (h7 === 0 || h7 === 1)) return true; // :: and ::1
  if ((h0 & 0xffc0) === 0xfe80) return true; // fe80::/10 link-local
  if ((h0 & 0xfe00) === 0xfc00) return true; // fc00::/7 unique local
  // IPv4-mapped (::ffff:a.b.c.d) and the deprecated IPv4-compatible (::a.b.c.d)
  // forms reach the embedded v4 address — judge them by the v4 rules.
  if (leadingZero && (h5 === 0xffff || h5 === 0)) {
    return isPrivateIpv4([h6 >> 8, h6 & 0xff, h7 >> 8, h7 & 0xff]);
  }
  return false;
}

type Ipv6Hextets = [number, number, number, number, number, number, number, number];

/** Expand an IPv6 literal (with `::` and an optional trailing dotted IPv4) to 8 hextets; null when malformed. */
function expandIpv6(literal: string): Ipv6Hextets | null {
  const halves = literal.split("::");
  if (halves.length > 2) return null;
  const toHextets = (part: string): number[] | null => {
    if (!part) return [];
    const out: number[] = [];
    for (const group of part.split(":")) {
      const ipv4 = parseIpv4(group);
      if (ipv4) {
        const [a, b, c, d] = ipv4;
        out.push((a << 8) | b, (c << 8) | d);
        continue;
      }
      if (!/^[0-9a-f]{1,4}$/.test(group)) return null;
      out.push(parseInt(group, 16));
    }
    return out;
  };
  const head = toHextets(halves[0] ?? "");
  const tail = halves.length === 2 ? toHextets(halves[1] ?? "") : [];
  if (!head || !tail) return null;
  const missing = 8 - head.length - tail.length;
  if (halves.length === 2 ? missing < 0 : missing !== 0) return null;
  const expanded = [...head, ...new Array<number>(Math.max(missing, 0)).fill(0), ...tail];
  return expanded.length === 8 ? (expanded as Ipv6Hextets) : null;
}

/**
 * Heuristic: only flag a snapshot as unrenderable when EVERY usable surface is
 * empty — title, description, headings, AND body. Many SPAs ship rich og: /
 * description meta tags even though their bodies are tiny `<div id="root">`
 * shells; those snapshots are still useful for the model. We only fall back
 * to today's behavior when there is genuinely nothing to read.
 */
export function looksLikeSpaShell(snapshot: {
  title?: string | null;
  description?: string | null;
  headings?: string[];
  bodyExcerpt: string;
}): boolean {
  const title = snapshot.title?.trim() ?? "";
  const description = snapshot.description?.trim() ?? "";
  const headings = (snapshot.headings ?? []).join(" ").trim();
  const body = snapshot.bodyExcerpt.replace(/\s+/g, " ").trim();
  const totalSignal = title.length + description.length + headings.length + body.length;
  return totalSignal < 80;
}
